import { createColumnHelper } from '@tanstack/react-table';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';

import type { InvalidPhoneEntry } from './phone-validate';

interface InvalidPhoneDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user acknowledges with "I understand". */
  onAcknowledge: () => void;
  entries: Array<InvalidPhoneEntry>;
}

const MAX_LISTED = 10;
const columnHelper = createColumnHelper<InvalidPhoneEntry>();
const columns = [
  columnHelper.accessor('rowNumber', { header: 'Row' }),
  columnHelper.accessor('field', { header: 'Field' }),
  columnHelper.accessor('value', {
    header: 'Value',
    cell: (info) => <span className='font-mono'>{info.getValue()}</span>,
  }),
  columnHelper.accessor('message', { header: 'Issue' }),
];

export function InvalidPhoneDialog({
  open,
  onOpenChange,
  onAcknowledge,
  entries,
}: InvalidPhoneDialogProps) {
  const listed = entries.slice(0, MAX_LISTED);
  const remaining = entries.length - listed.length;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {entries.length} phone number
            {entries.length === 1 ? '' : 's'} may be invalid
          </AlertDialogTitle>
          <AlertDialogDescription>
            We could not confirm the format of the phone numbers below. You can
            fix them in your CSV and re-upload, or continue and correct them
            later in Studio Portal.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <EnrollioTable
          totalLabel='Invalid phone numbers'
          filterHeader={(table) => (
            <EnrollioTableSearch table={table} placeholder='Search issues...' />
          )}
          data={listed}
          columns={columns}
          getRowId={(row, index) =>
            `${row.rowNumber}-${row.field}-${String(index)}`
          }
          paginationVariant='compact'
          emptyContent='No invalid phone numbers.'
          ariaLabel='Invalid phone numbers'
        />
        {remaining > 0 && (
          <p className='text-xs text-muted-foreground'>
            And {remaining} more. Fix these in your CSV for the most reliable
            import.
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              onAcknowledge();
            }}
          >
            I understand
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
